"use client";

import Image from "next/image";
import { StoryFilmButton } from "./story-film-button";
import type { TechStory } from "./tech-stories";
import { StoryVideo } from "./story-video";

export type StoryCardProps = {
  story: TechStory;
  index: number;
  active: boolean;
  autoPlay: boolean;
  shouldLoad: boolean;
  onBufferingChange: (buffering: boolean) => void;
  ctaLabel: string;
  /** Desktop only: opens the film. Without it the card has no CTA. */
  onWatchFilm?: () => void;
};

export function StoryCard({
  story,
  index,
  active,
  autoPlay,
  shouldLoad,
  onBufferingChange,
  ctaLabel,
  onWatchFilm,
}: StoryCardProps) {
  const n = index + 1;

  return (
    <article
      id={`tech-stories-card-${n}`}
      data-testid={`tech-stories-card-${n}`}
      className="flex h-full flex-col rounded-[20px] bg-base-11 p-1.5 lg:flex-row-reverse lg:items-center lg:gap-3 lg:bg-[linear-gradient(136.56deg,var(--black)_21.75%,#353534_79.98%)] lg:p-10 xl:p-16"
    >
      <div
        id={`tech-stories-card-${n}-media`}
        data-testid={`tech-stories-card-${n}-media`}
        className="relative aspect-square w-full shrink-0 overflow-hidden rounded-2xl bg-base-12 lg:w-[min(430px,42%)]"
      >
        {story.video ? (
          <StoryVideo
            video={story.video}
            active={active}
            autoPlay={autoPlay}
            shouldLoad={shouldLoad}
            onBufferingChange={onBufferingChange}
            className="absolute inset-0"
          />
        ) : story.image ? (
          <Image
            src={story.image}
            alt=""
            aria-hidden="true"
            fill
            sizes="(min-width: 1024px) 430px, min(354px, calc(100vw - 76px))"
            className="object-cover"
            style={
              story.imagePosition
                ? { objectPosition: story.imagePosition }
                : undefined
            }
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-3 px-[18px] py-6 lg:min-w-0 lg:flex-1 lg:gap-8 lg:p-0">
        <h3
          id={`tech-stories-card-${n}-title`}
          data-testid={`tech-stories-card-${n}-title`}
          className="title-20 max-w-[245px] text-white lg:title-32 lg:max-w-[425px] xl:title-40"
        >
          {story.title}
        </h3>
        <p
          id={`tech-stories-card-${n}-body`}
          data-testid={`tech-stories-card-${n}-body`}
          className="body-16 text-light-grey/80 lg:title-18 lg:max-w-[382px] lg:text-light-grey/70"
        >
          {story.body}
        </p>
        {onWatchFilm ? (
          <StoryFilmButton
            id={`tech-stories-card-${n}-cta`}
            label={ctaLabel}
            onClick={onWatchFilm}
            className="hidden lg:flex"
          />
        ) : null}
      </div>
    </article>
  );
}
